import { useIntl } from 'react-intl';
import {
    getTransportAssignmentSearchMode,
    getTransportAssignmentSearchTerm,
    SearchMode,
    setSearchTerm,
    transportAssignmentsSlice,
} from '../../../reducers/transportAssignmentsSlice';
import Select, { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { KeyboardEvent, useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import ClearableInput from '@rio-cloud/rio-uikit/ClearableInput';

export const TransportOrderToolbarSearch = () => {
    type SearchOption = SelectOption & {
        id: SearchMode;
        placeholder: string;
    };

    const intl = useIntl();
    const searchMode = useAppSelector(getTransportAssignmentSearchMode);
    const searchTerm = useAppSelector(getTransportAssignmentSearchTerm);
    const dispatch = useAppDispatch();

    const selectOptions: SearchOption[] = useMemo(
        () =>
            Object.values(SearchMode).map((mode) => ({
                id: mode,
                label: intl.formatMessage({
                    id: `outboundOrderBook.transportAssignments.table.toolbar.search.mode.${mode}.label`,
                }),
                placeholder: intl.formatMessage({
                    id: `outboundOrderBook.transportAssignments.table.toolbar.search.mode.${mode}.placeholder`,
                }),
            })),
        [intl],
    );

    const onSearchModeChange = useCallback(
        (option: SearchOption | undefined) => {
            if (option !== undefined) {
                dispatch(transportAssignmentsSlice.actions.setSearchMode(option.id));
            }
        },
        [dispatch],
    );

    const onSearchTermChange = useCallback(
        (newValue: string) => dispatch(setSearchTerm({ value: newValue })),
        [dispatch],
    );

    const onClear = useCallback(() => dispatch(setSearchTerm({ value: '', immediate: true })), [dispatch]);

    const onKeyPress = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                dispatch(setSearchTerm({ value: searchTerm, immediate: true }));
            }
        },
        [searchTerm, dispatch],
    );

    const placeholder = selectOptions.find(({ id }) => id === searchMode)?.placeholder;

    return (
        // The ClearableInput CSS supports buttons at the end but not at the start.
        // If one tries to add buttons on the start the input is still rounded on
        // that side. Therefore, the code uses the render prop to build a nested
        // input group with a select and an input like shown in the UI KIT:
        // https://uikit.developers.rio.cloud/#/components/select
        <ClearableInput
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={onSearchTermChange}
            onClear={onClear}
            onKeyPress={onKeyPress}
        >
            {(inputProps) => {
                return (
                    <span className="input-group width-500">
                        <span className="input-group-btn">
                            <Select
                                className="width-150"
                                options={selectOptions}
                                value={[searchMode]}
                                onChange={onSearchModeChange}
                            />
                        </span>
                        <input {...inputProps} />
                    </span>
                );
            }}
        </ClearableInput>
    );
};
